// app_client/src/pages/admin/FinancialAccountManagement.jsx
import React, { useEffect, useState } from 'react';
import { Landmark, Banknote, CreditCard, Edit3, Plus, Trash2, MapPin } from 'lucide-react';
import { toast } from 'react-hot-toast'; 

// Local Bileşenler ve Store
import useFinancialStore from '../../store/useFinancialStore';
import FinancialAccountFormModal from '../../components/modals/FinancialAccountFormModal';
import { useConfirmation } from '../../components/providers/ConfirmationProvider';

const FinancialAccountManagement = () => {
  const { confirm } = useConfirmation();
  const { accounts, loading, fetchAccounts, deleteAccount } = useFinancialStore();
  const safeAccounts = Array.isArray(accounts) ? accounts : [];

  // Modal ve Seçili Hesap State'leri
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  // Para birimi formatlayıcı (Hesabın kendi currency değerini kullanır)
  const formatCurrency = (val, currency = 'TRY') => 
    new Intl.NumberFormat('tr-TR', { 
      style: 'currency', 
      currency: currency 
    }).format(val || 0);

  // Hesap tipine göre görsel kimlik (Ikon ve Renk)
  const getAccountStyles = (type) => {
    switch (type) {
      case 'Bank': 
        return { icon: <Landmark size={24} />, color: 'text-blue-600', bg: 'bg-blue-50' };
      case 'CreditCard': 
        return { icon: <CreditCard size={24} />, color: 'text-purple-600', bg: 'bg-purple-50' };
      default: 
        return { icon: <Banknote size={24} />, color: 'text-emerald-600', bg: 'bg-emerald-50' };
    }
  };

  // İşlem Tetikleyicileri
  const handleEdit = (acc) => {
    setSelectedAccount(acc); 
    setIsModalOpen(true);
  };

  const handleCreate = () => {
    setSelectedAccount(null); 
    setIsModalOpen(true);
  };

  const handleDelete = async (id, balance) => {
    // Güvenlik: Bakiyesi olan hesap kapatılamaz
    if (balance !== 0) {
      return toast.error("Bakiyesi olan hesap kapatılamaz! Lütfen önce bakiyeyi sıfırlayın.");
    }
  
    if (await confirm("Bu finansal hesabı pasife almak istediğinize emin misiniz?")) {
      const success = await deleteAccount(id);
      if (success) toast.success("Hesap başarıyla kapatıldı.");
    }
  };

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-8">
      
      {/* 1. Form Modalı Entegrasyonu */}
      <FinancialAccountFormModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        initialData={selectedAccount} 
      />

      {/* 2. Header Bölümü */}
      <div className="flex justify-between items-end">
        <div>
          <h1 className="text-3xl font-black text-slate-800 tracking-tight">Finansal Hesaplar</h1>
          <p className="text-slate-500 font-bold uppercase text-[10px] tracking-widest mt-1 italic">
            Kasa, Banka ve Kredi Kartı Yönetimi
          </p>
        </div>
        <button 
          onClick={handleCreate}
          className="flex items-center gap-2 bg-slate-900 hover:bg-black text-white px-6 py-3 rounded-2xl font-black text-xs uppercase tracking-widest transition-all shadow-lg active:scale-95"
        >
          <Plus size={18} /> Yeni Hesap Tanımla
        </button>
      </div>

      {/* 3. Hesap Listesi (Grid) */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {loading ? (
          <div className="col-span-full py-20 text-center font-black text-slate-300 animate-pulse uppercase tracking-widest text-sm">
            Veriler Senkronize Ediliyor...
          </div>
        ) : (
          safeAccounts.map((acc) => {
            const styles = getAccountStyles(acc.type);
            return (
              <div key={acc._id} className="bg-white rounded-[2.5rem] border border-slate-100 p-8 shadow-sm hover:shadow-xl transition-all group relative overflow-hidden">
                
                {/* Kart Üst Bölüm: İkon ve Aksiyonlar */}
                <div className="flex justify-between items-start mb-6">
                  <div className={`p-4 rounded-2xl ${styles.bg} ${styles.color}`}>
                    {styles.icon}
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button 
                      onClick={() => handleEdit(acc)} 
                      className="p-2 text-slate-400 hover:text-blue-500 hover:bg-blue-50 rounded-xl transition-all"
                      title="Düzenle"
                    >
                      <Edit3 size={18} />
                    </button>
                    <button 
                      onClick={() => handleDelete(acc._id, acc.balance)} 
                      className="p-2 text-slate-400 hover:text-rose-500 hover:bg-rose-50 rounded-xl transition-all"
                      title="Hesabı Kapat"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>

                {/* Hesap Bilgileri */}
                <div className="space-y-1">
                  <h3 className="font-black text-slate-800 text-xl truncate tracking-tight">{acc.name}</h3>
                  {acc.type === 'Bank' && (
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1">
                      <MapPin size={10} className="text-blue-400" /> {acc.bankName} / {acc.branchName}
                    </p>
                  )}
                </div>

                {/* Bakiye Gösterimi */}
                <div className="mt-8 pt-6 border-t border-slate-50 flex flex-col">
                  <span className="text-[9px] font-black text-slate-300 uppercase tracking-[0.2em] mb-1">Anlık Kullanılabilir Bakiye</span>
                  <span className={`text-2xl font-black tracking-tight ${acc.balance < 0 ? 'text-rose-600' : 'text-slate-800'}`}>
                    {formatCurrency(acc.balance, acc.currency)}
                  </span>
                </div>

                {/* Banka Detayları (IBAN) - Sadece Banka Tipinde Görünür */}
                {acc.type === 'Bank' && acc.iban && (
                  <div className="mt-4 p-4 bg-slate-50/50 rounded-2xl border border-slate-100/50">
                    <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1">Uluslararası Hesap No (IBAN)</p>
                    <p className="text-[11px] font-mono font-bold text-slate-500 break-all leading-tight tracking-tighter">
                      {acc.iban.replace(/(.{4})/g, '$1 ')}
                    </p>
                  </div>
                )}

                {/* Durum Göstergesi */}
                <div className="absolute top-0 right-0 p-1">
                   <div className={`w-1.5 h-1.5 rounded-full ${acc.status === 'Active' ? 'bg-emerald-400' : 'bg-slate-300'}`} />
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Veri Yoksa Gösterilecek Durum */}
      {!loading && accounts.length === 0 && (
        <div className="py-20 text-center bg-slate-50 rounded-[3rem] border-2 border-dashed border-slate-100">
          <div className="max-w-xs mx-auto space-y-4">
            <div className="w-16 h-16 bg-white rounded-2xl shadow-sm flex items-center justify-center mx-auto text-slate-300">
              <Plus size={32} />
            </div>
            <p className="font-bold text-slate-400">Henüz tanımlı finansal hesabınız bulunmuyor.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinancialAccountManagement;
